import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import {
  clickupService,
  clickUpDocsQueryKey,
  clickUpListsQueryKey,
  clickUpMembersQueryKey,
  clickUpTeamsQueryKey,
} from "@/services/clickupService";
import type { CreateClickUpTaskRequest } from "@/types/clickup";

export function useClickUpTeams(enabled = true) {
  const { user } = useAuth();

  return useQuery({
    queryKey: clickUpTeamsQueryKey(user?.id),
    queryFn: () => clickupService.listTeams(),
    enabled: enabled && !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useClickUpMembers(teamId: string | null | undefined, enabled = true) {
  const { user } = useAuth();

  return useQuery({
    queryKey: clickUpMembersQueryKey(user?.id, teamId ?? undefined),
    queryFn: () => clickupService.listMembers(teamId!),
    enabled: enabled && !!user?.id && !!teamId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useClickUpLists(teamId: string | null | undefined, enabled = true) {
  const { user } = useAuth();

  return useQuery({
    queryKey: clickUpListsQueryKey(user?.id, teamId ?? undefined),
    queryFn: () => clickupService.listLists(teamId!),
    enabled: enabled && !!user?.id && !!teamId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateClickUpTasks() {
  return useMutation({
    mutationFn: (tasks: CreateClickUpTaskRequest[]) => clickupService.createTasks(tasks),
  });
}

export function useClickUpDocs(teamId: string | null | undefined, enabled = true) {
  const { user } = useAuth();

  return useQuery({
    queryKey: clickUpDocsQueryKey(user?.id, teamId ?? undefined),
    queryFn: () => clickupService.listDocs(teamId!),
    enabled: enabled && !!user?.id && !!teamId,
    staleTime: 5 * 60 * 1000,
  });
}
